import React, { useCallback, useEffect, useState } from 'react';
import { FiChevronLeft, FiChevronRight } from 'react-icons/fi';
import libbyPortrait from '../assets/characters/libby.png';
import beaPortrait from '../assets/characters/bea.png';
import remiRexPortrait from '../assets/characters/remi-rex.png';
import tulliePortrait from '../assets/characters/tullie.png';
import taszerPortrait from '../assets/characters/taszer.png';
import christyPortrait from '../assets/characters/christy.png';
import raxlePortrait from '../assets/characters/raxle.png';
import jDenPortrait from '../assets/characters/j-den.png';
import gibberPortrait from '../assets/characters/gibber.png';
import effiePortrait from '../assets/characters/effie.png';
import bbPortrait from '../assets/characters/bb-the-bear.png';
import lalaPortrait from '../assets/characters/lala-the-lemur.png';

export const CHARACTERS = [
  {
    id: 'libby',
    name: 'Libby the Lion',
    tagline: 'Kindness, songs & joy',
    bio: 'A cute and cuddly lioness cub with a heart of gold and wisdom beyond her years who loves to sing and spread joy and kindness.',
    theme: 'libby',
    portrait: libbyPortrait,
    portraitAlt: 'Libby the Lion',
  },
  {
    id: 'bea',
    name: 'Bea the Butterfly',
    tagline: 'Best friend on every adventure',
    bio: "A girl butterfly who is Libby's best friend and confidant on every adventure, matching Libby's yearn to do good and find the best in everyone and everything.",
    theme: 'bea',
    portrait: beaPortrait,
    portraitAlt: 'Bea the Butterfly',
  },
  {
    id: 'bb',
    name: 'BB the Bear',
    tagline: 'Bass lines & good vibes',
    bio: 'A cool, calm boy bear who loves making people groove to his bass lines.',
    theme: 'bb',
    portrait: bbPortrait,
    portraitAlt: 'BB the Bear',
    portraitSize: 'full',
  },
  {
    id: 'lala',
    name: 'LaLa the Lemur',
    tagline: 'Sing, dance & sparkle',
    bio: "A soul after Libby's own heart who loves to sing, dance, and just make everyone feel good.",
    theme: 'lala',
    portrait: lalaPortrait,
    portraitAlt: 'LaLa the Lemur',
    portraitSize: 'full',
  },
  {
    id: 'remi-rex',
    name: 'Remi & Rex the Raccoons',
    tagline: 'Twin trouble & teamwork',
    bio: 'Twin raccoons — Remi is a genius-level smart girl who loves solving problems and sports, though she is a little shy. Rex is an all-around jock with a cocky but reserved attitude.',
    theme: 'raccoon',
    portrait: remiRexPortrait,
    portraitAlt: 'Remi and Rex the Raccoons',
  },
  {
    id: 'tullie',
    name: 'Tullie the Turtle',
    tagline: 'Storyteller & jokester',
    bio: "A smart and quirky boy turtle, one of Libby's best friends, who loves to read — a storyteller and jokester for the ages.",
    theme: 'tullie',
    portrait: tulliePortrait,
    portraitAlt: 'Tullie the Turtle',
  },
  {
    id: 'taszer',
    name: 'Taszer the Tiger',
    tagline: 'Superheroes & big heart',
    bio: 'A mischievous boy tiger who loves superheroes and has a kind heart — though he sometimes has a little trouble sharing.',
    theme: 'taszer',
    portrait: taszerPortrait,
    portraitAlt: 'Taszer the Tiger',
  },
  {
    id: 'christy',
    name: 'Christy the Cardinal',
    tagline: 'Wisdom with a girly sparkle',
    bio: 'A girly-girl cardinal with wisdom and love in her heart who always has a bit of good advice.',
    theme: 'christy',
    portrait: christyPortrait,
    portraitAlt: 'Christy the Cardinal',
  },
  {
    id: 'raxle',
    name: 'Raxle the Rhino',
    tagline: 'Friendly grump, big heart',
    bio: 'A rambunctious and friendly grumpy boy rhino who is always going against the grain.',
    theme: 'raxle',
    portrait: raxlePortrait,
    portraitAlt: 'Raxle the Rhino',
  },
  {
    id: 'j-den',
    name: 'J-Den the Jackrabbit',
    tagline: 'Cool, calm & fun-loving',
    bio: 'A cool and laid-back, fun-loving jackrabbit who keeps the crew smiling.',
    theme: 'j-den',
    portrait: jDenPortrait,
    portraitAlt: 'J-Den the Jackrabbit',
  },
  {
    id: 'gibber',
    name: 'Gibber the Giraffe',
    tagline: 'Tall help, kind heart',
    bio: 'A kind boy giraffe who is always finding ways to help others with his height.',
    theme: 'gibber',
    portrait: gibberPortrait,
    portraitAlt: 'Gibber the Giraffe',
  },
  {
    id: 'effie',
    name: 'Effie the Elephant',
    tagline: 'Wise, fun & full of heart',
    bio: 'A wise and fun-loving girl elephant with a prissy style and a big heart.',
    theme: 'effie',
    portrait: effiePortrait,
    portraitAlt: 'Effie the Elephant',
  },
];

const INTERVAL_MS = 8000;

function CharacterPortrait({ character }) {
  if (character.portrait) {
    const sizeClass =
      character.portraitSize === 'full' ? ' char-carousel__portrait-img--full' : '';
    return (
      <img
        src={character.portrait}
        alt={character.portraitAlt || character.name}
        className={`char-carousel__portrait-img${sizeClass}`}
        decoding="async"
      />
    );
  }

  return <span className="char-carousel__glyph">{character.glyph}</span>;
}

export default function CharacterCarousel() {
  const [index, setIndex] = useState(0);
  const [paused, setPaused] = useState(false);
  const count = CHARACTERS.length;

  const go = useCallback(
    dir => {
      setIndex(prev => (prev + dir + count) % count);
    },
    [count]
  );

  const goTo = useCallback(
    i => {
      setIndex(((i % count) + count) % count);
    },
    [count]
  );

  useEffect(() => {
    if (paused) return undefined;
    const id = window.setInterval(() => go(1), INTERVAL_MS);
    return () => window.clearInterval(id);
  }, [paused, go]);

  return (
    <div
      className="char-carousel"
      role="region"
      aria-roledescription="carousel"
      aria-label="Meet the characters"
      onMouseEnter={() => setPaused(true)}
      onMouseLeave={() => setPaused(false)}
      onFocusCapture={() => setPaused(true)}
      onBlurCapture={e => {
        if (!e.currentTarget.contains(e.relatedTarget)) setPaused(false);
      }}
    >
      <div className="char-carousel__stage">
        {CHARACTERS.map((c, i) => (
          <article
            key={c.id}
            id={`character-${c.id}`}
            className={`char-carousel__slide${i === index ? ' is-active' : ''}`}
            aria-hidden={i !== index}
            tabIndex={i === index ? 0 : -1}
            aria-label={c.portraitAlt || c.name}
          >
            <div className={`char-carousel__card char-carousel__card--${c.theme}`}>
              <div className="char-carousel__portrait" aria-hidden>
                <CharacterPortrait character={c} />
              </div>
              <div className="char-carousel__body">
                <p className="char-carousel__tagline">{c.tagline}</p>
                <h3 className="char-carousel__name">{c.name}</h3>
                <p className="char-carousel__bio">{c.bio}</p>
              </div>
            </div>
          </article>
        ))}
      </div>

      <div className="char-carousel__controls">
        <button type="button" className="char-carousel__arrow" onClick={() => go(-1)} aria-label="Previous character">
          <FiChevronLeft size={20} aria-hidden />
        </button>
        <div className="char-carousel__dots" role="tablist" aria-label="Choose character">
          {CHARACTERS.map((c, i) => (
            <button
              key={c.id}
              type="button"
              role="tab"
              aria-selected={i === index}
              aria-controls={`character-${c.id}`}
              className={`char-carousel__dot${i === index ? ' is-active' : ''}`}
              onClick={() => goTo(i)}
            >
              <span className="visually-hidden">{c.name}</span>
            </button>
          ))}
        </div>
        <button type="button" className="char-carousel__arrow" onClick={() => go(1)} aria-label="Next character">
          <FiChevronRight size={20} aria-hidden />
        </button>
      </div>
    </div>
  );
}
